import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  Bell,
  PanelLeftOpen,
  LogOut,
  Settings,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTheme } from '../../design/ThemeContext';
import ThemeToggle from '../ui/ThemeToggle';

function getPageTitle(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Dashboard';
  return segments[segments.length - 1]
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { toggleSidebar, sidebarCollapsed } = useAppTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const menuRef = useRef(null);

  const handleLogout = async () => {
    setMenuOpen(false);
    try {
      await logout();
      navigate('/');
    } catch (err) {
      navigate('/');
    }
  };

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const pageTitle = getPageTitle(location.pathname);
  const breadcrumbs = location.pathname.split('/').filter(Boolean);

  return (
    <header
      className="sticky top-0 z-30 glass-navbar"
      style={{ height: 'var(--navbar-height)' }}
    >
      <div className="flex items-center justify-between h-full px-4 md:px-6">
        {/* Left side */}
        <div className="flex items-center gap-3">
          {/* Sidebar toggle */}
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center rounded-xl hover-btn"
            style={{
              width: 38,
              height: 38,
              border: '1px solid var(--border-primary)',
              background: 'var(--bg-input)',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: sidebarCollapsed ? 'flex' : undefined,
            }}
          >
            <PanelLeftOpen size={18} />
          </button>

          {/* Breadcrumb + Title */}
          <div className="hidden sm:flex flex-col">
            <div className="flex items-center gap-1">
              {breadcrumbs.map((crumb, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight size={12} style={{ color: 'var(--text-muted)' }} />}
                  <span
                    className="text-[11px] font-medium uppercase tracking-wider capitalize"
                    style={{
                      color: i === breadcrumbs.length - 1 ? 'var(--accent-primary)' : 'var(--text-muted)',
                    }}
                  >
                    {crumb.replace(/-/g, ' ')}
                  </span>
                </React.Fragment>
              ))}
            </div>
            <h2
              style={{
                fontWeight: 700,
                color: 'var(--text-primary)',
                fontSize: '1.1rem',
                lineHeight: 1.2,
                letterSpacing: '-0.01em',
                margin: 0,
              }}
            >
              {pageTitle}
            </h2>
          </div>
        </div>

        {/* Trial banner */}
        {user?.subscription_info?.is_trial && (
          <button
            onClick={() => navigate('/pricing')}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: 'rgba(139, 92, 246, 0.12)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              color: '#8B5CF6',
              cursor: 'pointer',
            }}
          >
            <span>Pro Trial: {user.subscription_info.days_remaining}d left</span>
            <ChevronRight size={12} />
          </button>
        )}

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <div
            className="hidden md:flex items-center gap-2 rounded-xl transition-all duration-300"
            style={{
              padding: '6px 14px',
              backgroundColor: searchFocused ? 'var(--bg-card)' : 'var(--bg-input)',
              border: `1px solid ${searchFocused ? 'var(--border-accent)' : 'var(--border-primary)'}`,
              width: searchFocused ? 260 : 200,
              boxShadow: searchFocused ? '0 0 0 3px var(--accent-glow)' : 'none',
            }}
          >
            <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search..."
              className="bg-transparent border-none outline-none text-sm w-full"
              style={{ color: 'var(--text-primary)', fontFamily: 'inherit' }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
          </div>

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Notifications */}
          <button
            className="relative flex items-center justify-center rounded-xl hover-btn"
            style={{
              width: 38,
              height: 38,
              border: '1px solid var(--border-primary)',
              background: 'var(--bg-input)',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
          >
            <Bell size={18} />
            <span
              className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full"
              style={{ background: 'var(--color-error)' }}
            />
          </button>

          {/* User Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((p) => !p)}
              className="flex items-center justify-center rounded-xl hover-card"
              style={{
                padding: 4,
                border: '1px solid var(--border-primary)',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <div
                className="flex items-center justify-center rounded-lg"
                style={{
                  width: 30,
                  height: 30,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  background: 'var(--gradient)',
                  color: '#fff',
                }}
              >
                {user?.email?.[0]?.toUpperCase() || 'U'}
              </div>
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 rounded-xl overflow-hidden shadow-xl animate-fade-in"
                style={{
                  width: 220,
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                  zIndex: 50,
                }}
              >
                <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                    {user?.username || user?.email || 'User'}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                    {user?.email || ''}
                  </p>
                </div>
                <div className="py-1">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/settings');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover-nav"
                    style={{ color: 'var(--text-secondary)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    <Settings size={16} style={{ color: 'var(--text-muted)' }} />
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover-error"
                    style={{ color: 'var(--color-error)', background: 'transparent', border: 'none', cursor: 'pointer', transition: 'background 0.15s ease' }}
                  >
                    <LogOut size={16} />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
